import React, { useState, useEffect } from 'react';
import HomeTopBar from './HomeTopBar';
import TotalFirstMentionAwarenessChart from '../charts/FirstMentionAwareness/TotalFirstMentionAwarenessChart';
import FirstMentionAwarenessPrivateChart from '../charts/FirstMentionAwareness/FirstMentionAwarenessPrivateChart';
import FirstMentionAwarenessYouthChart from '../charts/FirstMentionAwareness/FirstMentionAwarenessYouthChart';
import FirstMentionAwarenessGranularChart from '../charts/FirstMentionAwareness/FirstMentionAwarenessGranularCharts';
import FirstMentionPPTX from '../powerpoint/FirstMentionPPTX';
import axios from 'axios';
import { provinces, local_market, province_market_map } from '../constants';

const FirstMentionAwareness = ({ heading, total, total_youth, total_private, prestige, private_only, youth, middle_market, mass_market }) => {
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedLocalMarket, setSelectedLocalMarket] = useState('');
  const [localMarkets, setLocalMarkets] = useState([]);
  const [chartsToDisplay, setChartsToDisplay] = useState('total-display');
  const [FirstMentionChartDataMain, setFirstMentionChartDataMain] = useState([]);
  const [FirstMentionChartDataPrivate, setFirstMentionChartDataPrivate] = useState([]);
  const [FirstMentionChartDataYouthAndMiddle, setFirstMentionChartDataYouthAndMiddle] = useState([]);
  const [FirstMentionChartDataPrestigeOnly, setFirstMentionChartDataPrestigeOnly] = useState([]);
  const [FirstMentionChartDataPrivateOnly, setFirstMentionChartDataPrivateOnly] = useState([]);
  const [FirstMentionChartDataYouthOnly, setFirstMentionChartDataYouthOnly] = useState([]);
  const [FirstMentionChartDataMassMarketOnly, setFirstMentionChartDataMassMarketOnly] = useState([]);
  const [FirstMentionChartDataMiddleMarketOnly, setFirstMentionChartDataMiddleMarketOnly] = useState([]);

  const handleProvinceChange = (e) => {
    const selectedProvince = e.target.value;
    setSelectedProvince(selectedProvince);
    
    if (selectedProvince && province_market_map[selectedProvince]) {
      setLocalMarkets(province_market_map[selectedProvince].local_markets);
    } else {
      setLocalMarkets([]); 
    }
    setSelectedLocalMarket(''); 
  };


  const getProvinceValue = () => {
    return selectedProvince ? provinces[selectedProvince] : ''; 
  };

  const getLocalMarketValue = () => {
    return selectedLocalMarket ? local_market[selectedLocalMarket] : ''; 
  };

  useEffect(() => {
    const fetchData = async () => {
      const provinceValue = getProvinceValue();
      const localMarketValue = getLocalMarketValue();

      const endpointMapping = [
        { endpoint: 'total', setter: setFirstMentionChartDataMain },
        { endpoint: 'youth_main_and_middle', setter: setFirstMentionChartDataYouthAndMiddle },
        { endpoint: 'total_private', setter: setFirstMentionChartDataPrivate },
        { endpoint: 'youth', setter: setFirstMentionChartDataYouthOnly },
        { endpoint: 'mass_market', setter: setFirstMentionChartDataMassMarketOnly },
        { endpoint: 'middle_market', setter: setFirstMentionChartDataMiddleMarketOnly },
        { endpoint: 'prestige', setter: setFirstMentionChartDataPrestigeOnly },
        { endpoint: 'private', setter: setFirstMentionChartDataPrivateOnly }
      ];

      try {
        const promises = endpointMapping.map(({ endpoint }) =>
          axios.get(`https://outlook.plus94research.co.za/api/first-mention-awareness/${endpoint}/`, {
            params: {
               province: provinceValue,
               local_market: localMarketValue,
            }
          })
        );

        const responses = await Promise.all(promises);
        responses.forEach((response, index) => {
          endpointMapping[index].setter(response.data.chart_data);
        });

        console.log("All data fetched successfully");
      } catch (error) {
        console.error('Error fetching data:', error);
        endpointMapping.forEach(({ setter }) => setter([]));
      }
    };

    fetchData();
  }, [selectedProvince, selectedLocalMarket]);

  const handleChartDisplayChange = (displayType) => {
    setChartsToDisplay(displayType);
  };

  return (
    <div className='home'>
      <HomeTopBar />
      <div className='three-charts-heading-div'>
        <h2 className='heading'>{heading}</h2>
      </div>

      <div className='filter'>
        <label htmlFor="province-select">Province: </label>
        <select
          id="province-select"
          value={selectedProvince}
          onChange={handleProvinceChange}
        >
          <option value="">All</option>
          {Object.keys(provinces).map((province) => (
            <option key={province} value={province}>
              {province}
            </option>
          ))}
        </select>

        <label htmlFor="local-market-select">Local Market: </label>
        <select
          id="local-market-select"
          value={selectedLocalMarket}
          onChange={(e) => setSelectedLocalMarket(e.target.value)}
          disabled={!selectedProvince || localMarkets.length === 0}
        >
          <option value="">All</option>
          {localMarkets.map((market) => (
            <option key={market} value={market}>
              {market}
            </option>
          ))}
        </select>
      </div>

      <div>
        <FirstMentionPPTX
          selectedProvince={selectedProvince}
          selectedLocalMarket={selectedLocalMarket}
          maindata={FirstMentionChartDataMain}
          privatedata={FirstMentionChartDataPrivate}
          youthdata={FirstMentionChartDataYouthAndMiddle}
          prestigeonlydata={FirstMentionChartDataPrestigeOnly}
          privateonlydata={FirstMentionChartDataPrivateOnly}
        />
      </div>

      <div className='three-charts-container'>
        <div className='three-charts-left-container'>
          <h2 className='main-bank-total'>{total}</h2>
          <TotalFirstMentionAwarenessChart
            selectedProvince={selectedProvince}
            selectedLocalMarket={selectedLocalMarket}
            data={FirstMentionChartDataMain}
          />
        </div>

        {chartsToDisplay === "total-display" && (
          <div className='three-charts-right-container'>
            <div className='total-youth-mass-and-middle'>
              <h2 
                className='main-bank-total-youth' 
                onClick={() => handleChartDisplayChange('youth-mass-and-middle-only')}
                style={{ cursor: 'pointer' }}
              >
                {total_youth}
              </h2>
              <FirstMentionAwarenessYouthChart
                selectedProvince={selectedProvince}
                selectedLocalMarket={selectedLocalMarket}
                data={FirstMentionChartDataYouthAndMiddle}
              />
            </div>

            <div className='total-private'>
              <h2 
                className='main-bank-total-private'
                onClick={() => handleChartDisplayChange('private-and-prestige')}
                style={{ cursor: 'pointer' }}
              >
                {total_private}
              </h2>
              <FirstMentionAwarenessPrivateChart
                selectedProvince={selectedProvince}
                selectedLocalMarket={selectedLocalMarket}
                data={FirstMentionChartDataPrivate}
              />
            </div>
          </div>
        )}

        {chartsToDisplay === "private-and-prestige" && (
          <div className='private-and-prestige-only'>
            <div>
              <h2 className='prestige-only' onClick={() => handleChartDisplayChange('total-display')} style={{ cursor: 'pointer' }}>{prestige}</h2>
              <FirstMentionAwarenessGranularChart
                selectedProvince={selectedProvince}
                selectedLocalMarket={selectedLocalMarket}
                data={FirstMentionChartDataPrestigeOnly}
                id="prestige-only"
              />
            </div>
            <div>
              <h2 className='private-only' onClick={() => handleChartDisplayChange('total-display')} style={{ cursor: 'pointer' }}>{private_only}</h2>
              <FirstMentionAwarenessGranularChart
                selectedProvince={selectedProvince}
                selectedLocalMarket={selectedLocalMarket}
                data={FirstMentionChartDataPrivateOnly}
                id="private-only"
              />
            </div>
          </div>
        )}

        {chartsToDisplay === "youth-mass-and-middle-only" && (
          <div className='youth-mass-and-middle-only'>
            <div>
              <h2 className='youth-only' onClick={() => handleChartDisplayChange('total-display')} style={{ cursor: 'pointer' }}>{youth}</h2>
              <FirstMentionAwarenessGranularChart
                selectedProvince={selectedProvince}
                selectedLocalMarket={selectedLocalMarket}
                data={FirstMentionChartDataYouthOnly}
                id="youth-only"
              />
            </div>
            <div>
              <h2 className='mass-market-only' onClick={() => handleChartDisplayChange('total-display')} style={{ cursor: 'pointer' }}>{mass_market}</h2>
              <FirstMentionAwarenessGranularChart
                selectedProvince={selectedProvince}
                selectedLocalMarket={selectedLocalMarket}
                data={FirstMentionChartDataMassMarketOnly}
                id="mass-market-only"
              />
            </div>
            <div>
              <h2 className='middle-market-only' onClick={() => handleChartDisplayChange('total-display')} style={{ cursor: 'pointer' }}>{middle_market}</h2>
              <FirstMentionAwarenessGranularChart
                selectedProvince={selectedProvince}
                selectedLocalMarket={selectedLocalMarket}
                data={FirstMentionChartDataMiddleMarketOnly}
                id="middle-market-only"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstMentionAwareness;
